import { useI18n } from '../../../i18n';
// ============================================
// ProductFilterBar Component
// Filter bar with debounced search
// ============================================

import { useState, useEffect } from 'react';
import { SelectDropdown, SelectOption } from '../../../components/shared';
import { SearchInput } from '../../../components/shared/SearchInput';

export interface ProductFilters {
  search: string;
  categoryId: string | null;
  priceMin: string;
  priceMax: string;
  status: string | null;
  supplierId?: string | null;
}

interface ProductFilterBarProps {
  filters: ProductFilters;
  onFiltersChange: (filters: ProductFilters) => void;
  onClearFilters: () => void;
  categories?: SelectOption[];
  suppliers?: SelectOption[];
  isLoading?: boolean;
}

export function ProductFilterBar({
  filters,
  onFiltersChange,
  suppliers = [],
  isLoading,
}: ProductFilterBarProps) {
  const { t } = useI18n();
  // Local search state - updates filters immediately (client-side filtering)
  const [localSearch, setLocalSearch] = useState(filters.search);

  // Sync if cleared externally
  useEffect(() => {
    if (filters.search === '') setLocalSearch('');
  }, [filters.search]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    onFiltersChange({ ...filters, search: value });
  };

  const statusOptions: SelectOption[] = [
    { value: '', label: t('allStatus') },
    { value: 'ACTIVE', label: t('productsActive') },
    { value: 'INACTIVE', label: t('productsInactive') },
  ];

  const supplierOptions: SelectOption[] = [
    { value: '', label: t('allSuppliers') },
    ...suppliers,
  ];

  const updateFilter = <K extends keyof ProductFilters>(key: K, value: ProductFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      {/* Search */}
      <SearchInput
        value={localSearch}
        onChange={handleSearchChange}
        placeholder={t('searchProducts')}
        width="260px"
      />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Suppliers */}
      <SelectDropdown
        options={supplierOptions}
        value={filters.supplierId ?? null}
        onChange={(value) => updateFilter('supplierId', value || null)}
        placeholder={t('allSuppliers')}
        width="200px"
        size="md"
      />

      {/* Status */}
      <SelectDropdown
        options={statusOptions}
        value={filters.status}
        onChange={(value) => updateFilter('status', value || null)}
        placeholder={t('allStatus')}
        width="180px"
        size="md"
      />
    </div>
  );
}
